import { t as uiText } from '../../i18n';
import React from 'react';
import './EmbedInstallGuide.css';

export type EmbedInstallGuideKind = 'direct' | 'gtm';

interface EmbedInstallGuideProps {
  kind: EmbedInstallGuideKind;
  /** 登録済みのサイト。あれば「このサイトでだけ動く」として表示する。 */
  allowedOrigins?: string[];
}

interface GuideStep {
  text: string;
  hint?: string;
}

function guideContent(kind: EmbedInstallGuideKind): {
  audience: string;
  caution?: string;
  steps: GuideStep[];
  verify: string[];
  troubles: string[];
} {
  if (kind === 'gtm') {
    return {
      audience: uiText("You already use Google Tag Manager (GTM) on your site."),
      caution: uiText("Always paste this tab's code into GTM. Pasting the Direct embed code as is can keep the button from appearing."),
      steps: [
        { text: uiText("Press \"Copy\" at the top right of the code.") },
        { text: uiText("Open GTM and choose your site's container.") },
        { text: uiText("Go to \"Tags\" → \"New\" → \"Tag Configuration\" and choose \"Custom HTML\".") },
        { text: uiText("Paste the code into the HTML field.") },
        { text: uiText("Under \"Triggering\", choose \"All Pages\".") },
        { text: uiText("Open \"Advanced Settings\" → \"Tag firing options\" and choose \"Once per page\".") },
        { text: uiText("Save, check it in \"Preview\", then press \"Submit\" to publish.") },
      ],
      verify: [
        uiText("Open one of your registered sites and reload the page. The button should appear."),
      ],
      troubles: [
        uiText("You pasted the Direct embed code. Replace it with this tab's code."),
        uiText("You have not submitted (published) in GTM yet."),
        uiText("Safari's private browsing blocks GTM itself. If it must work there, use the Direct embed code."),
        uiText("An older notreu tag is still in GTM, so the button appears twice. Delete the extra tag."),
      ],
    };
  }
  return {
    audience: uiText("You can edit the HTML of your site, or you can ask the person who built it to do it."),
    steps: [
      { text: uiText("Press \"Copy\" at the top right of the code.") },
      { text: uiText("Open the screen where you edit your site's HTML.") },
      {
        text: uiText("Paste the code in a part shared by every page, just before </body>. Pasting it inside <head> also works."),
        hint: uiText("If you use a site builder, paste it into a field such as \"Custom code\" or \"Footer script\". The name differs by service."),
      },
      { text: uiText("Save, then publish your site.") },
    ],
    verify: [
      uiText("Open one of your registered sites and reload the page. The button should appear."),
      uiText("After you change the design in the admin screen, it can take up to 5 minutes to show on your site."),
    ],
    troubles: [
      uiText("You are viewing an address that is not registered."),
      uiText("Only part of the code was copied."),
      uiText("You pasted this code into Google Tag Manager (GTM). For GTM, use the \"Recommended for GTM\" tab instead."),
      uiText("A company network or ad blocker is blocking it. Try another network, or turn the blocker off for a moment."),
    ],
  };
}

const EmbedInstallGuide: React.FC<EmbedInstallGuideProps> = ({ kind, allowedOrigins = [] }) => {
  const content = guideContent(kind);

  return (
    <div className="embed-install-guide" data-kind={kind}>
      <section className="embed-install-guide__section">
        <h4 className="embed-install-guide__title">{uiText("Who this is for")}</h4>
        <p className="embed-install-guide__text">{content.audience}</p>
        {content.caution ? (
          <p className="embed-install-guide__caution">{content.caution}</p>
        ) : null}
      </section>

      <section className="embed-install-guide__section">
        <h4 className="embed-install-guide__title">{uiText("Before you start")}</h4>
        <ul className="embed-install-guide__list">
          <li>{uiText("After you change settings in the admin screen, it can take time before they show on the screen (up to 5 minutes).")}</li>
          <li>{uiText("After a change, refresh the browser before you check.")}</li>
          <li>
            {uiText("This code works only on the sites you registered.")}
            {allowedOrigins.length > 0 ? (
              <ul className="embed-install-guide__origins">
                {allowedOrigins.map((origin) => (
                  <li key={origin}><code>{origin}</code></li>
                ))}
              </ul>
            ) : null}
            <span className="embed-install-guide__hint">
              {uiText("https://example.com and https://www.example.com count as different sites. Register both if you use both.")}
            </span>
          </li>
          <li>{uiText("Paste the code in one place only. Pasting it twice, or in both Direct embed and GTM, shows the button twice.")}</li>
          <li>{uiText("Copy the whole code without changing a single character.")}</li>
        </ul>
      </section>

      <section className="embed-install-guide__section">
        <h4 className="embed-install-guide__title">{uiText("Steps")}</h4>
        <ol className="embed-install-guide__steps">
          {content.steps.map((step) => (
            <li key={step.text}>
              {step.text}
              {step.hint ? <span className="embed-install-guide__hint">{step.hint}</span> : null}
            </li>
          ))}
        </ol>
        <p className="embed-install-guide__text">
          {uiText("If someone else manages your site, send them this screen's code as is and ask them to follow these steps.")}
        </p>
      </section>

      <section className="embed-install-guide__section">
        <h4 className="embed-install-guide__title">{uiText("Check that it works")}</h4>
        <ul className="embed-install-guide__list">
          {content.verify.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      </section>

      <section className="embed-install-guide__section">
        <h4 className="embed-install-guide__title">{uiText("If the button does not appear")}</h4>
        <ul className="embed-install-guide__list">
          {content.troubles.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      </section>
    </div>
  );
};

export default EmbedInstallGuide;
